import React, {
  useState,
  useCallback,
  useContext,
} from 'react';
import PropTypes from 'prop-types';
import firebase from 'firebase/app';
import { observer } from 'mobx-react';
import * as Sentry from '@sentry/browser';
import format from 'string-template';
import useToast from '../../../../hooks/useToast';
import useUserDoc from '../../../../hooks/useUserDoc';
import ExternalCoachContext from '../../../../context/ExternalCoachContext';
import OnHoldWorkoutReview from '../../../../Model/OnHoldWorkoutReview';
import ConfirmDialog from '../../../../components/ConfirmDialog';
import DialogModal from '../../../../../components/DialogModal';
import ExercisesTable from '../../../WorkoutsManager/Exercises/ExercisesTable';
import AvatarSizeType from '../../../../../components/Avatar/AvatarSizes';
import {
  CardWrapper,
  StyledUserProfileCard,
  ConflictBlock,
  BoxRow,
  RestrictedBox,
  ReplacementBox,
  BoxLabel,
  BoxValue,
  ArrowGlyph,
  MatchInfo,
  MatchInfoText,
  MatchInfoQuote,
  ScopeInfo,
  ButtonRow,
  ActionButton,
  PickerButton,
  ExerciseChangeContainer,
  ExerciseChangeTitle,
  BlockedBanner,
  BlockedBannerTitle,
  BlockedBannerIds,
  SuggestedTag,
  DuplicateTag,
  DuplicateSummary,
} from './styles';
import texts from './texts.json';

const OnHoldWorkoutCard = ({ onHoldWorkoutReview }) => {
  const { showToast } = useToast();
  const [skipping, setSkipping] = useState(false);
  const [applying, setApplying] = useState(false);
  const [skipConfirmOpen, setSkipConfirmOpen] = useState(false);
  const [applyConfirmOpen, setApplyConfirmOpen] = useState(false);
  const { conflicts = [] } = onHoldWorkoutReview;
  const [selections, setSelections] = useState(() => conflicts.reduce((acc, c) => {
    if (c.suggestedReplacement) {
      acc[c.exerciseId] = {
        id: c.suggestedReplacement.exerciseId,
        label: c.suggestedReplacement.name,
        fromSuggestion: true,
      };
    }
    return acc;
  }, {}));
  const [activePickerConflictId, setActivePickerConflictId] = useState(null);
  const { coachId } = useContext(ExternalCoachContext);

  const conflictCount = conflicts.length;
  const userId = onHoldWorkoutReview.user;
  const isBlocked = onHoldWorkoutReview.isBlocked === true;
  const blockedByIds = onHoldWorkoutReview.isBlockedBy || [];
  const blockedByLabels = onHoldWorkoutReview.isBlockedByLabels || {};
  const { displayLabel, occurrenceCount } = onHoldWorkoutReview;
  // Conflict review is coach-scoped: pin the viewed coach for per-coach reads.
  const { userDoc } = useUserDoc(userId, { viewAsCoachId: coachId });
  const clientName = `${userDoc?.data?.firstName || ''} ${userDoc?.data?.lastName || ''}`.trim();
  // Coach-scoped via the viewAsCoachId pin above, so a multi-coach client's
  // submission for another coach can't satisfy this feed's check.
  const hasPostPaymentFormAnswer = !!userDoc?.postPaymentFormSubmittedAt;

  const allFilled = conflicts.length > 0 && conflicts.every((c) => !!selections[c.exerciseId]);

  // The warning follows the suggested exercise: it stays while that exercise is selected (even when re-picked
  // by hand) and clears once the coach picks a different one. The server works the flag out once, per workout,
  // from the suggestions; the card has no workout contents, so it can't re-check rows live. Two known limits:
  // when two rows were flagged for sharing a suggestion and one is re-picked, the other keeps its (now stale)
  // warning; and an exercise picked by hand is never checked.
  const isDuplicateSuggestion = (conflict) => conflict.suggestedReplacement?.isDuplicate === true
    && selections[conflict.exerciseId]?.id === conflict.suggestedReplacement.exerciseId;
  const duplicateCount = conflicts.filter(isDuplicateSuggestion).length;
  let duplicateSummaryText = '';
  if (duplicateCount === 1) {
    duplicateSummaryText = texts.duplicateSummarySingular;
  } else if (duplicateCount > 1) {
    duplicateSummaryText = format(texts.duplicateSummaryPlural, { count: duplicateCount });
  }

  const openPicker = useCallback((conflictId) => {
    setActivePickerConflictId(conflictId);
  }, []);

  const closePicker = useCallback(() => {
    setActivePickerConflictId(null);
  }, []);

  const handleExerciseSelect = useCallback((selectedExercise) => {
    if (!activePickerConflictId || !selectedExercise) {
      setActivePickerConflictId(null);
      return;
    }
    setSelections((prev) => ({
      ...prev,
      [activePickerConflictId]: {
        id: selectedExercise.id,
        label: selectedExercise.name || '',
      },
    }));
    setActivePickerConflictId(null);
  }, [activePickerConflictId]);

  const openSkipConfirm = useCallback(() => {
    if (skipping || applying || isBlocked) return;
    setSkipConfirmOpen(true);
  }, [skipping, applying, isBlocked]);

  const openApplyConfirm = useCallback(() => {
    if (skipping || applying || !allFilled || isBlocked) return;
    setApplyConfirmOpen(true);
  }, [skipping, applying, allFilled, isBlocked]);

  const closeSkipConfirm = useCallback(() => setSkipConfirmOpen(false), []);
  const closeApplyConfirm = useCallback(() => setApplyConfirmOpen(false), []);

  const handleSkipConfirm = useCallback(async () => {
    if (skipping || applying) return;
    setSkipConfirmOpen(false);
    setSkipping(true);
    try {
      const idToken = await firebase.auth().currentUser.getIdToken();
      await onHoldWorkoutReview.resolve(idToken);
      showToast(texts.skipSuccess);
    } catch (error) {
      Sentry.captureException(error);
      showToast(texts.skipError, { error: true });
    } finally {
      setSkipping(false);
    }
  }, [skipping, applying, onHoldWorkoutReview, showToast]);

  const handleApplyConfirm = useCallback(async () => {
    if (skipping || applying) return;
    setApplyConfirmOpen(false);
    setApplying(true);
    try {
      const overrides = conflicts.map((c) => ({
        originalExerciseId: c.exerciseId,
        overrideExerciseId: selections[c.exerciseId].id,
      }));
      const idToken = await firebase.auth().currentUser.getIdToken();
      await onHoldWorkoutReview.resolve(idToken, overrides);
      showToast(texts.applySuccess);
    } catch (error) {
      Sentry.captureException(error);
      showToast(texts.applyError, { error: true });
    } finally {
      setApplying(false);
    }
  }, [skipping, applying, conflicts, selections, onHoldWorkoutReview, showToast]);

  const exercisesLabel = conflictCount === 1
    ? texts.skipConfirmDialog.exercisesSingular
    : texts.skipConfirmDialog.exercisesPlural;

  const reviewCountText = conflictCount === 1
    ? `1 ${texts.needsReviewSuffix}`
    : `${conflictCount} ${texts.needsReviewSuffixPlural}`;
  const occurrencesText = occurrenceCount === 1
    ? texts.occurrencesSingular
    : format(texts.occurrencesPlural, { count: occurrenceCount });

  const subtitleParts = [];
  if (displayLabel) subtitleParts.push(displayLabel);
  if (occurrenceCount > 0) subtitleParts.push(occurrencesText);
  subtitleParts.push(reviewCountText);
  const subtitleText = subtitleParts.join(' · ');

  const isBusy = skipping || applying;
  const interactionDisabled = isBusy || isBlocked;

  return (
    <CardWrapper $blocked={isBlocked}>
      <StyledUserProfileCard
        clientId={userId}
        avatarSize={AvatarSizeType.SMALL}
        dataRow={{
          userName: clientName || userId,
          userAvatar: userDoc?.avatarUrl,
          serviceStartAt: userDoc?.serviceStartAt,
          postPaymentFormSubmittedAt: userDoc?.postPaymentFormSubmittedAt?.toDate(),
          // Root read on purpose: isAIFriendEnabled lives on the root flags rollup,
          // not the entry flags the `flags` getter now returns.
          isAIFriendEnabled: !!userDoc?.isAIFriendEnabled,
        }}
        subTitle={subtitleText}
        hasPostPaymentFormAnswer={hasPostPaymentFormAnswer}
      />

      {isBlocked && blockedByIds.length > 0 && (
        <BlockedBanner>
          <BlockedBannerTitle>{texts.blockedTitle}</BlockedBannerTitle>
          <div>{texts.blockedDescription}</div>
          <BlockedBannerIds>
            {blockedByIds.map((id, idx) => (
              <React.Fragment key={id}>
                {idx > 0 && ', '}
                <span>{blockedByLabels[id] || `…${id.slice(-4)}`}</span>
              </React.Fragment>
            ))}
          </BlockedBannerIds>
        </BlockedBanner>
      )}

      {conflicts.map((conflict) => {
        const selected = selections[conflict.exerciseId] || null;
        const isPicked = !!selected;
        const isDuplicate = isDuplicateSuggestion(conflict);
        return (
          <ConflictBlock key={conflict.exerciseId}>
            <BoxRow>
              <RestrictedBox>
                <BoxLabel>{texts.restrictedExerciseLabel}</BoxLabel>
                <BoxValue>{conflict.exerciseName}</BoxValue>
              </RestrictedBox>
              <ArrowGlyph>→</ArrowGlyph>
              <ReplacementBox $picked={isPicked} $duplicate={isDuplicate}>
                <BoxLabel $accent={isPicked && !isDuplicate}>
                  {isPicked ? texts.yourReplacementLabel : texts.pickReplacementLabel}
                </BoxLabel>
                <PickerButton
                  type="button"
                  onClick={() => openPicker(conflict.exerciseId)}
                  disabled={interactionDisabled}
                  $picked={isPicked}
                >
                  {isPicked ? selected.label : texts.pickerPlaceholder}
                </PickerButton>
                {isDuplicate && (
                  <DuplicateTag>{texts.duplicateLabel}</DuplicateTag>
                )}
                {!!selected?.fromSuggestion && !isDuplicate && (
                  <SuggestedTag>{texts.suggestedLabel}</SuggestedTag>
                )}
              </ReplacementBox>
            </BoxRow>
            <MatchInfo>
              <MatchInfoText>{`${texts.matchedOnPrefix} `}</MatchInfoText>
              <MatchInfoQuote>
                &quot;
                {conflict.matchedRestriction}
                &quot;
              </MatchInfoQuote>
              <MatchInfoText>{` ${texts.matchedOnSuffix}`}</MatchInfoText>
            </MatchInfo>
          </ConflictBlock>
        );
      })}

      <ScopeInfo>{texts.scopeInfo}</ScopeInfo>

      <ButtonRow>
        {!!duplicateSummaryText && (
          <DuplicateSummary>{duplicateSummaryText}</DuplicateSummary>
        )}
        <ActionButton
          variant="warning"
          onClick={openSkipConfirm}
          disabled={interactionDisabled}
          type="button"
        >
          {skipping ? texts.skipButtonInProgress : format(texts.skipButton, { count: occurrenceCount })}
        </ActionButton>
        <ActionButton
          variant="success"
          onClick={openApplyConfirm}
          disabled={!allFilled || interactionDisabled}
          type="button"
        >
          {applying
            ? texts.applyReplacementsInProgress
            : format(texts.applyReplacementsButton, { count: conflictCount })}
        </ActionButton>
      </ButtonRow>

      <ConfirmDialog
        isOpen={skipConfirmOpen}
        onConfirm={handleSkipConfirm}
        onCancel={closeSkipConfirm}
        confirmDisabled={isBusy}
        dialogTexts={{
          title: texts.skipConfirmDialog.title,
          content: format(texts.skipConfirmDialog.description, {
            count: conflictCount,
            exercises: exercisesLabel,
          }),
          confirm: texts.skipConfirmDialog.confirm,
          cancel: texts.skipConfirmDialog.cancel,
        }}
      />
      <ConfirmDialog
        isOpen={applyConfirmOpen}
        onConfirm={handleApplyConfirm}
        onCancel={closeApplyConfirm}
        confirmDisabled={isBusy}
        dialogTexts={{
          title: texts.applyConfirmDialog.title,
          content: [
            format(texts.applyConfirmDialog.description, {
              count: conflictCount,
              exercises: exercisesLabel,
            }),
            duplicateSummaryText,
          ].filter(Boolean).join(' '),
          confirm: texts.applyConfirmDialog.confirm,
          cancel: texts.applyConfirmDialog.cancel,
        }}
      />

      <DialogModal
        open={!!activePickerConflictId}
        onClose={closePicker}
        fullWidth
        maxWidth="md"
      >
        <ExerciseChangeContainer>
          <ExerciseChangeTitle>{texts.pickerModalTitle}</ExerciseChangeTitle>
          <ExercisesTable
            isExerciseChangeView
            handleActionClick={handleExerciseSelect}
          />
        </ExerciseChangeContainer>
      </DialogModal>
    </CardWrapper>
  );
};

OnHoldWorkoutCard.propTypes = {
  onHoldWorkoutReview: PropTypes.instanceOf(OnHoldWorkoutReview).isRequired,
};

export default observer(OnHoldWorkoutCard);
